import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { z } from "zod";

const frameworkSchema = z.object({
  title: z.string().trim().min(1, "Title is required").max(200, "Title must be less than 200 characters"),
  description: z.string().trim().min(10, "Description must be at least 10 characters").max(1000, "Description must be less than 1000 characters"),
  content: z.string().max(50000, "Content must be less than 50000 characters").optional().or(z.literal("")),
  cover_image_url: z.string().optional().or(z.literal("")),
  pdf_url: z.string().optional().or(z.literal(""))
});

interface Framework {
  id: string;
  title: string;
  description: string;
  cover_image_url?: string;
  pdf_url?: string;
  slug: string;
  content?: string;
  created_at: string;
}

const isDev = import.meta.env.DEV;

export const FrameworksManager = () => {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFramework, setEditingFramework] = useState<Framework | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    cover_image_url: '',
    pdf_url: ''
  });
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  useEffect(() => {
    loadFrameworks();
  }, []);

  const loadFrameworks = async () => {
    try {
      const { data } = await supabase
        .from("frameworks")
        .select("*")
        .order("created_at", { ascending: false });

      if (data) setFrameworks(data);
    } catch (error) {
      if (isDev) console.error("Error loading frameworks:", error);
      toast({ 
        title: "Error", 
        description: "Failed to load frameworks", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (framework?: Framework) => {
    if (framework) {
      setEditingFramework(framework);
      setFormData({
        title: framework.title,
        description: framework.description,
        cover_image_url: framework.cover_image_url || "",
        pdf_url: framework.pdf_url || "",
        content: framework.content || "",
      });
    } else {
      setEditingFramework(null);
      setFormData({
        title: "",
        description: "",
        cover_image_url: "",
        pdf_url: "",
        content: "",
      });
    }
    setCoverImageFile(null);
    setPdfFile(null);
    setIsDialogOpen(true);
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate input data first
      frameworkSchema.parse(formData);
      
      const slug = generateSlug(formData.title);
      let finalFormData = { ...formData };

      // Upload cover image if provided
      if (coverImageFile) {
        const fileExt = coverImageFile.name.split('.').pop();
        const fileName = `framework-covers/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('pescelab-main-site')
          .upload(fileName, coverImageFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('pescelab-main-site')
          .getPublicUrl(fileName);

        finalFormData.cover_image_url = publicUrl;
      }

      // Upload PDF if provided
      if (pdfFile) {
        const fileName = `framework-pdfs/${Date.now()}-${pdfFile.name}`;
        
        const { error: uploadError } = await supabase.storage
          .from('pescelab-main-site')
          .upload(fileName, pdfFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('pescelab-main-site')
          .getPublicUrl(fileName);

        finalFormData.pdf_url = publicUrl;
      }
      
      if (editingFramework) {
        await supabase
          .from("frameworks")
          .update({
            title: finalFormData.title,
            description: finalFormData.description,
            cover_image_url: finalFormData.cover_image_url || null,
            pdf_url: finalFormData.pdf_url || null,
            content: finalFormData.content || null,
            slug,
          })
          .eq("id", editingFramework.id);
      } else {
        await supabase
          .from("frameworks")
          .insert({
            title: finalFormData.title,
            description: finalFormData.description,
            cover_image_url: finalFormData.cover_image_url || null,
            pdf_url: finalFormData.pdf_url || null,
            content: finalFormData.content || null,
            slug,
          });
      }

      setFormData({
        title: "",
        description: "",
        cover_image_url: "",
        pdf_url: "",
        content: "",
      });
      setCoverImageFile(null);
      setPdfFile(null);
      setEditingFramework(null);
      setIsDialogOpen(false);
      loadFrameworks();
      
      toast({
        title: "Success",
        description: `Framework ${editingFramework ? "updated" : "created"} successfully`,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        if (isDev) console.error("Error saving framework:", error);
        toast({
          title: "Error",
          description: "Failed to save framework",
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("frameworks")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Framework deleted successfully",
      });
      loadFrameworks();
    } catch (error) {
      if (isDev) console.error("Error deleting framework:", error);
      toast({
        title: "Error",
        description: "Failed to delete framework",
        variant: "destructive",
      });
    }
  };

  if (loading) return <div>Loading frameworks...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Frameworks</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openEditDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Framework
            </Button>
          </DialogTrigger>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{editingFramework ? 'Edit Framework' : 'Add New Framework'}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto pr-1">
              <form onSubmit={handleSubmit} className="space-y-6 pb-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Short Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="min-h-[80px]"
                  />
                </div>

                <div>
                  <Label htmlFor="cover-image">Cover Image</Label>
                  <Input
                    id="cover-image"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={(e) => setCoverImageFile(e.target.files?.[0] || null)}
                  />
                  {formData.cover_image_url && (
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground mb-2">Current cover image:</p>
                      <img 
                        src={formData.cover_image_url} 
                        alt="Cover preview" 
                        className="w-32 h-32 object-cover rounded border"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-3 p-4 border border-primary/20 rounded-lg bg-primary/5">
                  <Label htmlFor="pdf-upload" className="text-base font-semibold text-primary">PDF Upload</Label>
                  <p className="text-sm text-muted-foreground">
                    Upload a PDF file that users can download after providing their email
                  </p>
                  <Input
                    id="pdf-upload"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    className="border-primary/30 focus:border-primary"
                  />
                  {formData.pdf_url && (
                    <div className="mt-3 p-3 bg-background/50 rounded border">
                      <p className="text-sm font-medium text-foreground mb-2">Current PDF:</p>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigator.clipboard.writeText(formData.pdf_url)}
                          className="text-xs"
                        >
                          Copy URL
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(formData.pdf_url, '_blank')}
                          className="text-xs"
                        >
                          View PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="content">Supporting Article</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Write your framework content in Markdown format..."
                    className="min-h-[200px]"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    This content will be displayed below the download section on the framework landing page. Markdown supported.
                  </p>
                </div>

                <div className="flex-shrink-0 pt-4 border-t">
                  <Button type="submit" disabled={!formData.title.trim() || !formData.description.trim()} className="w-full">
                    {editingFramework ? 'Update Framework' : 'Add Framework'}
                  </Button>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>
        </Dialog>
      </div>

      <div className="grid gap-4 overflow-x-auto">
        {frameworks.map((framework) => (
          <Card key={framework.id}>
            <CardHeader>
              <CardTitle>{framework.title}</CardTitle>
              <CardDescription>{framework.description}</CardDescription>
              {framework.content && (
                <p className="text-sm text-muted-foreground mt-2">
                  Content: {framework.content.substring(0, 100)}...
                </p>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <h4 className="font-semibold text-primary">PDF Download</h4>
                
                {framework.pdf_url && (
                  <div className="flex items-center gap-2 p-2 bg-primary/5 rounded border border-primary/20">
                    <span className="text-sm font-medium text-primary min-w-[60px]">PDF:</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigator.clipboard.writeText(framework.pdf_url || '')}
                      className="text-xs h-7 border-primary/30 text-primary hover:bg-primary/10"
                    >
                      Copy URL
                    </Button>
                    <Button
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open(framework.pdf_url, '_blank')}
                      className="text-xs h-7 border-primary/30 text-primary hover:bg-primary/10"
                    >
                      View PDF
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditDialog(framework)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Framework</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{framework.title}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(framework.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};